import { useTranslation } from "react-i18next";

import { Checkbox, TableDataCell, Tooltip } from "@digital-services/ui";

import { UserState } from "@organization/models/EssentialUser";
import { OrganizationUser } from "@organization/models/OrganizationUser";
import { UserStatusIcon } from "@users/components/UserStatusIcon";
import { EditUserButton } from "@users/organizationuser/components/EditUserButton";
import { ReInviteUser } from "@users/organizationuser/components/ReInviteUser";

import { useUserContext } from "@hooks/useUserContext";

interface Props {
  organizationUser: OrganizationUser; // TBD Introduce context
  isChecked: boolean;
  toggleRowCheckbox: (val: OrganizationUser) => void;
}

export const OrganizationUserDataRow = (props: Readonly<Props>) => {
  const { organizationUser, isChecked, toggleRowCheckbox } = props;

  const { user: currentUser } = useUserContext();

  const { t } = useTranslation(undefined, { keyPrefix: "organization.users" });

  function getDateFormatter(dateStyle: "short" | "long") {
    return new Intl.DateTimeFormat(currentUser.language, {
      dateStyle: dateStyle
    });
  }

  const renderSelectionColumn = (user: OrganizationUser) => {
    if (!currentUser.isAdministrator) return "";

    // When label=undefined, then disabled=true DOES NOT make Checkbox disabled.
    // To be fixed in @digital-services/ui > v3.0.11
    // Workaround is to use label="", which cause a Checkbox to reflect disabled and checked fields
    return (
      <TableDataCell className="w-1">
        <div data-testid={user.id}>
          <Checkbox
            id={user.id}
            data-testid={`checkbox-${user.id}`}
            variant="alternative"
            disabled={user.id === currentUser.id}
            label="" // "" is IMPORTANT here
            checked={isChecked}
            onChange={() => {
              toggleRowCheckbox(user);
            }}
          />
        </div>
      </TableDataCell>
    );
  };

  const renderAColumn = (value: string) => {
    return <TableDataCell>{value}</TableDataCell>;
  };

  const renderStatusColumn = (state: UserState) => {
    return (
      <TableDataCell>
        <div className="flex gap-2">
          <UserStatusIcon state={state} />
          {t(`state.${state}`)}
        </div>
      </TableDataCell>
    );
  };

  const renderDateColumn = (date: Date) => {
    return (
      <TableDataCell>
        <Tooltip message={getDateFormatter("long").format(new Date(date))}>
          {getDateFormatter("short").format(new Date(date))}
        </Tooltip>
      </TableDataCell>
    );
  };

  const renderActionsColumn = (organizationUser: OrganizationUser) => {
    const isAdmin = currentUser.isAdministrator;

    return (
      <TableDataCell className="py-1!">
        {isAdmin ? (
          <div>
            <EditUserButton organizationUser={organizationUser} />
            {organizationUser.state === UserState.InvitationExpired ? (
              <ReInviteUser user={organizationUser} />
            ) : null}
          </div>
        ) : (
          <div></div>
        )}
      </TableDataCell>
    );
  };

  return (
    <tr>
      {renderSelectionColumn(organizationUser)}
      {renderAColumn(organizationUser.displayName)}
      {renderAColumn(organizationUser.emailAddress)}
      {renderAColumn(organizationUser.organizationRole)}
      {renderDateColumn(organizationUser.createdOn)}
      {renderStatusColumn(organizationUser.state)}
      {renderActionsColumn(organizationUser)}
    </tr>
  );
};
